import { get, push, ref, update } from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import { getPathDatabaseByRole } from "./auth";
import { db } from "../lib/firebase";
import { LaunchError } from "../utils/errors";
import { deleteFolderAndUnlinkContents } from "./folderDeletion";
import {
  ADS_CAMPAIGN_FOLDER_CHILDREN_ROOT,
  ADS_CAMPAIGN_FOLDER_CONTENTS_ROOT,
  ADS_CAMPAIGN_FOLDERS_ROOT,
  CONTENT_FOLDER_ROOT_ID,
} from "../utils/constants";
import {
  isDuplicateFolderName,
  normalizeFolderName,
  normalizeParentFolderID,
} from "../utils/contentFolderUtils";
import { releaseOwnListener } from "../utils/releaseOwnListener";
import { buildAdsCampaignFolderMembershipUpdates } from "../utils/adsCampaignFolderUpdates";

const getAdsCampaignFoldersPath = ({ advertiserID }) =>
  `${ADS_CAMPAIGN_FOLDERS_ROOT}/${advertiserID}`;

const getAdsCampaignFolderPath = ({ advertiserID, folderID }) =>
  `${getAdsCampaignFoldersPath({ advertiserID })}/${folderID}`;

const getAdsCampaignFolderChildrenPath = ({ advertiserID, parentFolderID }) =>
  `${ADS_CAMPAIGN_FOLDER_CHILDREN_ROOT}/${advertiserID}/${parentFolderID}`;

export const startListenerAdsCampaignFolders =
  ({ onUpdatedDataCallback = () => {}, onReferenceAvailable = () => {} }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID) {
        onUpdatedDataCallback({});
        return false;
      }

      const dbRef = ref(db, getAdsCampaignFoldersPath({ advertiserID }));
      const unsubscribe = onValue(dbRef, (snapshot) => {
        onUpdatedDataCallback(snapshot.val() || {});
      });
      onReferenceAvailable(unsubscribe);

      return unsubscribe;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerAdsCampaignFolders",
          tags: { action: "adsCampaignFolders" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerAdsCampaignFolders =
  ({ unsubscribe } = {}) =>
  () => {
    releaseOwnListener(unsubscribe);
  };

export const startCreateAdsCampaignFolder =
  ({ name, parentFolderID = CONTENT_FOLDER_ROOT_ID }) =>
  async (dispatch) => {
    const normalizedName = normalizeFolderName(name);
    const normalizedParentID = normalizeParentFolderID(parentFolderID);

    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID || !normalizedName) {
        return { ok: false, reason: "invalidName" };
      }

      const foldersPath = getAdsCampaignFoldersPath({ advertiserID });
      const foldersSnapshot = await get(ref(db, foldersPath));
      const folders = Object.entries(foldersSnapshot.val() || {}).map(
        ([id, folder]) => ({ id, ...folder })
      );

      if (
        isDuplicateFolderName({
          folders,
          parentFolderID: normalizedParentID,
          name: normalizedName,
        })
      ) {
        return { ok: false, reason: "duplicateName" };
      }

      const folderID = push(ref(db, foldersPath)).key;
      const now = Date.now();
      const folder = {
        name: normalizedName,
        parentFolderID: normalizedParentID,
        creationTime: now,
        updatedTime: now,
      };

      await update(ref(db), {
        [getAdsCampaignFolderPath({ advertiserID, folderID })]: folder,
        [`${getAdsCampaignFolderChildrenPath({
          advertiserID,
          parentFolderID: normalizedParentID,
        })}/${folderID}`]: true,
      });

      return {
        ok: true,
        data: {
          id: folderID,
          ...folder,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startCreateAdsCampaignFolder",
          context: { name: normalizedName, parentFolderID: normalizedParentID },
          tags: { action: "adsCampaignFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

const normalizeCampaignRecords = (campaignRecords = []) => {
  const recordsByID = new Map();

  campaignRecords.forEach((record) => {
    const campaignID = String(record?.campaignID || "");
    if (!campaignID || recordsByID.has(campaignID)) return;
    recordsByID.set(campaignID, {
      campaignID,
      previousFolderID: record.previousFolderID || null,
    });
  });

  return [...recordsByID.values()];
};

export const startGetAdsCampaignFolderContentIDs =
  ({ folderID }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (!advertiserID || !folderID) return [];

      const snapshot = await get(
        ref(
          db,
          `${ADS_CAMPAIGN_FOLDER_CONTENTS_ROOT}/${advertiserID}/${folderID}`
        )
      );
      return Object.keys(snapshot.val() || {}).sort();
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetAdsCampaignFolderContentIDs",
          context: { folderID },
          tags: { action: "adsCampaignFolders" },
          level: "error",
        })
      );
      return [];
    }
  };

export const startMoveAdsCampaignsToFolder =
  ({ campaignRecords, folderID }) =>
  async (dispatch) => {
    const records = normalizeCampaignRecords(campaignRecords);

    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID || !folderID || records.length === 0) {
        return { ok: false };
      }

      const targetFolderSnapshot = await get(
        ref(db, getAdsCampaignFolderPath({ advertiserID, folderID }))
      );
      if (!targetFolderSnapshot.exists()) {
        return { ok: false, reason: "folderNotFound" };
      }

      const { updates, campaignIDs } = buildAdsCampaignFolderMembershipUpdates({
        advertiserID,
        campaignRecords: records,
        folderID,
      });

      if (campaignIDs.length === 0) {
        return { ok: false, reason: "noChanges" };
      }

      await update(ref(db), updates);

      return {
        ok: true,
        data: {
          campaignIDs,
          folderID,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startMoveAdsCampaignsToFolder",
          context: {
            campaignIDs: records.map(({ campaignID }) => campaignID),
            folderID,
          },
          tags: { action: "adsCampaignFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startUnassignAdsCampaignsFromFolder =
  ({ campaignRecords }) =>
  async (dispatch) => {
    const records = normalizeCampaignRecords(campaignRecords);

    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID || records.length === 0) {
        return { ok: false };
      }

      const { updates, campaignIDs } = buildAdsCampaignFolderMembershipUpdates({
        advertiserID,
        campaignRecords: records,
        folderID: null,
      });

      if (campaignIDs.length === 0) {
        return { ok: false, reason: "noChanges" };
      }

      await update(ref(db), updates);

      return {
        ok: true,
        data: {
          campaignIDs,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startUnassignAdsCampaignsFromFolder",
          context: { campaignIDs: records.map(({ campaignID }) => campaignID) },
          tags: { action: "adsCampaignFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startRenameAdsCampaignFolder =
  ({ folderID, name }) =>
  async (dispatch) => {
    const normalizedName = normalizeFolderName(name);

    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID || !folderID || !normalizedName) {
        return { ok: false, reason: "invalidName" };
      }

      const folderSnapshot = await get(
        ref(db, getAdsCampaignFolderPath({ advertiserID, folderID }))
      );

      if (!folderSnapshot.exists()) {
        return { ok: false, reason: "folderNotFound" };
      }

      const currentFolder = folderSnapshot.val();
      const foldersPath = getAdsCampaignFoldersPath({ advertiserID });
      const foldersSnapshot = await get(ref(db, foldersPath));
      const folders = Object.entries(foldersSnapshot.val() || {}).map(
        ([id, folder]) => ({ id, ...folder })
      );

      if (
        isDuplicateFolderName({
          folders,
          parentFolderID: currentFolder.parentFolderID,
          name: normalizedName,
          excludeFolderID: folderID,
        })
      ) {
        return { ok: false, reason: "duplicateName" };
      }

      const now = Date.now();
      await update(ref(db), {
        [`${getAdsCampaignFolderPath({ advertiserID, folderID })}/name`]:
          normalizedName,
        [`${getAdsCampaignFolderPath({ advertiserID, folderID })}/updatedTime`]:
          now,
      });

      return {
        ok: true,
        data: {
          id: folderID,
          name: normalizedName,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startRenameAdsCampaignFolder",
          context: { folderID, name: normalizedName },
          tags: { action: "adsCampaignFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startDeleteAdsCampaignFolder =
  ({ folderID }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      return await deleteFolderAndUnlinkContents({
        advertiserID,
        folderID,
        foldersRoot: ADS_CAMPAIGN_FOLDERS_ROOT,
        childrenRoot: ADS_CAMPAIGN_FOLDER_CHILDREN_ROOT,
        contentsRoot: ADS_CAMPAIGN_FOLDER_CONTENTS_ROOT,
        getEntityPaths: (id) => [`adsCampaigns/${advertiserID}/${id}`],
      });
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startDeleteAdsCampaignFolder",
          context: { folderID },
          tags: { action: "adsCampaignFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };
